import React, { useEffect, useRef, useState } from 'react';
import {
  Dimensions,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { AdminColors } from '../../../constants/adminColors';
import FontFamily from '../../../../../constants/fonts';

const MIN_MENU_WIDTH = 220;

export interface FilterOption {
  id: string | number;
  displayText: string;
}

export interface LocationFilterDropdownProps {
  label: string;
  options: FilterOption[];
  value: FilterOption | FilterOption[] | null;
  onChange: (value: FilterOption | FilterOption[] | null) => void;
  isMulti?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
  isLoading?: boolean;
  showLabelInTrigger?: boolean;
  size?: 'default' | 'compact';
  searchOptions?: (searchText: string) => Promise<FilterOption[]>;
  searchDebounceMs?: number;
}

const LocationFilterDropdown: React.FC<LocationFilterDropdownProps> = ({
  label,
  options,
  value,
  onChange,
  isMulti = false,
  isDisabled = false,
  placeholder = 'Select...',
  isLoading = false,
  showLabelInTrigger = true,
  size = 'default',
  searchOptions,
  searchDebounceMs = 300,
}) => {
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [remoteOptions, setRemoteOptions] = useState<FilterOption[]>([]);
  const [searching, setSearching] = useState(false);
  const btnRef = useRef<any>(null);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0, width: 0 });
  const scrollListenerRef = useRef<(() => void) | null>(null);
  const menuMaxHeight = Dimensions.get('window').height * 0.45;

  const selectedArray = Array.isArray(value) ? value : (value ? [value] : []);
  const isActive = selectedArray.length > 0;
  const displayLabel = (() => {
    if (showLabelInTrigger) {
      if (!isActive) {
        return label;
      }

      return isMulti
        ? `${label} (${selectedArray.length})`
        : selectedArray[0]?.displayText || label;
    }

    if (!isActive) {
      return placeholder;
    }

    if (isMulti) {
      if (selectedArray.length === 1) {
        return selectedArray[0]?.displayText || placeholder;
      }

      return `${selectedArray.length} Selected`;
    }

    return selectedArray[0]?.displayText || placeholder;
  })();

  const query = searchText.trim().toLowerCase();
  const hasRemoteSearch = typeof searchOptions === 'function';

  useEffect(() => {
    if (!hasRemoteSearch) {
      setRemoteOptions([]);
      setSearching(false);
      return;
    }

    if (!open) {
      setRemoteOptions([]);
      setSearching(false);
      return;
    }

    const trimmedQuery = searchText.trim();
    if (trimmedQuery === '') {
      setRemoteOptions([]);
      setSearching(false);
      return;
    }

    let cancelled = false;
    setSearching(true);

    const timer = setTimeout(async () => {
      try {
        const results = await searchOptions(trimmedQuery);
        if (!cancelled) {
          setRemoteOptions(results);
        }
      } catch {
        if (!cancelled) {
          setRemoteOptions([]);
        }
      } finally {
        if (!cancelled) {
          setSearching(false);
        }
      }
    }, searchDebounceMs);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [hasRemoteSearch, open, searchOptions, searchDebounceMs, searchText]);

  let filteredOptions = hasRemoteSearch && query ? remoteOptions : [...options];
  if (!hasRemoteSearch && query) {
    filteredOptions = options
      .filter(opt => opt.displayText.toLowerCase().includes(query))
      .sort((a, b) => {
        const aText = a.displayText.toLowerCase();
        const bText = b.displayText.toLowerCase();

        const aExact = aText === query;
        const bExact = bText === query;
        if (aExact && !bExact) return -1;
        if (!aExact && bExact) return 1;

        const aStarts = aText.startsWith(query);
        const bStarts = bText.startsWith(query);
        if (aStarts && !bStarts) return -1;
        if (!aStarts && bStarts) return 1;

        return aText.localeCompare(bText);
      });
  }

  const setDropdownOpen = (nextOpen: boolean) => {
    setOpen(nextOpen);
  };

  const recalculatePosition = () => {
    if (Platform.OS !== 'web') return;
    if (!btnRef.current || typeof btnRef.current.getBoundingClientRect !== 'function') return;

    try {
      const APPROX_MENU_HEIGHT = 320;
      const screenH = Dimensions.get('window').height;
      const rect = btnRef.current.getBoundingClientRect();
      const spaceBelow = screenH - rect.bottom;
      const flipped = spaceBelow < APPROX_MENU_HEIGHT + 8;

      setDropPos({
        top: flipped ? rect.top - APPROX_MENU_HEIGHT : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
      });
    } catch {
      // ignore
    }
  };

  useEffect(() => {
    if (Platform.OS !== 'web') return;

    if (open) {
      const handler = () => recalculatePosition();

      if (typeof window !== 'undefined') {
        window.addEventListener('scroll', handler, true);
        window.addEventListener('resize', handler);

        const raf = window.requestAnimationFrame(() => recalculatePosition());

        scrollListenerRef.current = () => {
          window.removeEventListener('scroll', handler, true);
          window.removeEventListener('resize', handler);
          window.cancelAnimationFrame(raf);
        };
      }
    } else {
      if (scrollListenerRef.current) {
        scrollListenerRef.current();
        scrollListenerRef.current = null;
      }
    }

    return () => {
      if (scrollListenerRef.current) {
        scrollListenerRef.current();
        scrollListenerRef.current = null;
      }
    };
  }, [open]);

  const handleOpen = () => {
    if (isDisabled || isLoading) return;
    if (open) {
      setDropdownOpen(false);
      setSearchText('');
      return;
    }

    const APPROX_MENU_HEIGHT = 320;
    const screenH = Dimensions.get('window').height;

    if (Platform.OS !== 'web') {
      if (btnRef.current && typeof btnRef.current.measureInWindow === 'function') {
        btnRef.current.measureInWindow((x: number, y: number, w: number, h: number) => {
          const spaceBelow = screenH - (y + h);
          const flipped = spaceBelow < APPROX_MENU_HEIGHT + 8;
          setDropPos({
            top: flipped ? y - APPROX_MENU_HEIGHT : y + h + 4,
            left: x,
            width: w,
          });
          setDropdownOpen(true);
        });
      } else {
        setDropdownOpen(true);
      }
    } else {
      try {
        if (btnRef.current && typeof btnRef.current.getBoundingClientRect === 'function') {
          const rect = btnRef.current.getBoundingClientRect();
          const spaceBelow = screenH - rect.bottom;
          const flipped = spaceBelow < APPROX_MENU_HEIGHT + 8;

          setDropPos({
            top: flipped ? rect.top - APPROX_MENU_HEIGHT : rect.bottom + 4,
            left: rect.left,
            width: rect.width,
          });
        }
        setDropdownOpen(true);
      } catch {
        setDropdownOpen(true);
      }
    }
  };

  const handleSelect = (option: FilterOption) => {
    if (isMulti) {
      const isSelected = selectedArray.some(s => String(s.id) === String(option.id));
      const newValue = isSelected
        ? selectedArray.filter(s => String(s.id) !== String(option.id))
        : [...selectedArray, option];
      onChange(newValue.length > 0 ? newValue : null);
    } else {
      const isSelected = selectedArray[0]?.id === option.id;
      onChange(isSelected ? null : option);
      setDropdownOpen(false);
      setSearchText('');
    }
  };

  const handleSelectAll = () => {
    if (!isMulti) return;

    if (searchText.trim()) {
      const filteredIds = new Set(filteredOptions.map(item => String(item.id).toLowerCase()));
      const existingNonFiltered = selectedArray.filter(
        s => !filteredIds.has(String(s.id).toLowerCase())
      );
      const newValue = [...existingNonFiltered, ...filteredOptions];
      onChange(newValue.length > 0 ? newValue : null);
    } else {
      onChange(options.length > 0 ? [...options] : null);
    }
  };

  const handleClear = () => {
    onChange(null);
    if (!isMulti) {
      setDropdownOpen(false);
    }
    setSearchText('');
  };

  const handleClose = () => {
    setDropdownOpen(false);
    setSearchText('');
    setRemoteOptions([]);
    setSearching(false);
  };

  const isSelected = (option: FilterOption) =>
    selectedArray.some(s => String(s.id).toLowerCase() === String(option.id).toLowerCase());

  const getDisplayText = () => {
    if (selectedArray.length === 0) {
      return label;
    }

    if (isMulti) {
      return `${label} (${selectedArray.length})`;
    }

    return selectedArray[0]?.displayText || label;
  };

  return (
    <View style={styles.container}>
      <TouchableOpacity
        ref={btnRef}
        style={[
          styles.trigger,
          size === 'compact' && styles.triggerCompact,
          (open || isActive) && styles.triggerActive,
          (isDisabled || isLoading) && styles.triggerDisabled,
        ]}
        onPress={handleOpen}
        activeOpacity={0.7}
        disabled={isDisabled || isLoading}
      >
        <Text
          style={[
            styles.triggerText,
            size === 'compact' && styles.triggerTextCompact,
            !isActive && styles.triggerPlaceholder,
          ]}
          numberOfLines={1}
        >
          {isLoading ? 'Loading...' : displayLabel}
        </Text>
        <MaterialIcons
          name={open ? 'expand-less' : 'expand-more'}
          size={size === 'compact' ? 18 : 20}
          color={isDisabled ? '#ADB5BD' : '#495057'}
        />
      </TouchableOpacity>

      <Modal
        visible={open}
        transparent
        animationType="fade"
        supportedOrientations={['portrait', 'landscape']}
        onRequestClose={handleClose}
      >
        <TouchableOpacity style={styles.overlay} activeOpacity={1} onPress={handleClose}>
          <TouchableOpacity
            activeOpacity={1}
            style={[
              styles.menu,
              {
                top: dropPos.top,
                left: dropPos.left,
                width: Math.max(dropPos.width || 240, MIN_MENU_WIDTH),
              },
            ]}
            onPress={e => e.stopPropagation()}
          >
            <View style={styles.stickyHeader}>
              <View style={styles.searchRow}>
                <MaterialIcons name="search" size={18} color="#7C8086" />
                <TextInput
                  style={[
                    styles.searchInput,
                    Platform.OS === 'web' ? ({ outlineStyle: 'none' } as any) : null,
                  ]}
                  placeholder="Search..."
                  placeholderTextColor="#9AA1AB"
                  value={searchText}
                  onChangeText={setSearchText}
                />
                {searchText ? (
                  <TouchableOpacity onPress={() => setSearchText('')}>
                    <MaterialIcons name="close" size={16} color="#6D737C" />
                  </TouchableOpacity>
                ) : null}
              </View>

              <View style={[styles.actionsRow, !isMulti && styles.actionsRowCentered]}>
                {isMulti && (
                  <>
                    <TouchableOpacity onPress={handleSelectAll} style={styles.actionButton}>
                      <Text style={styles.actionText}>Select All</Text>
                    </TouchableOpacity>
                    <View style={styles.actionDivider} />
                  </>
                )}
                <TouchableOpacity onPress={handleClear} style={styles.actionButton}>
                  <Text style={styles.actionText}>Clear</Text>
                </TouchableOpacity>
              </View>
            </View>

            {Platform.OS === 'web' ? (
              <View style={styles.list} key={`list-${searchText.trim().toLowerCase()}`}>
                {filteredOptions.length === 0 ? (
                  <Text style={styles.emptyText}>{searching ? 'Searching...' : placeholder}</Text>
                ) : (
                  filteredOptions.map((opt, index) => {
                    const selected = isSelected(opt);
                    return (
                      <TouchableOpacity
                        key={`${String(opt.id)}-${opt.displayText}-${index}`}
                        style={[styles.item, selected && styles.itemSelected]}
                        onPress={() => handleSelect(opt)}
                        activeOpacity={0.7}
                      >
                        {isMulti ? (
                          <View style={[styles.checkbox, selected && styles.checkboxChecked]}>
                            {selected && <MaterialIcons name="check" size={14} color="#FFFFFF" />}
                          </View>
                        ) : null}
                        <Text style={styles.itemText} numberOfLines={1}>
                          {opt.displayText}
                        </Text>
                        {!isMulti && selected && (
                          <MaterialIcons name="check" size={16} color="#0B5FC6" />
                        )}
                      </TouchableOpacity>
                    );
                  })
                )}
              </View>
            ) : (
              <ScrollView
                style={styles.listNative}
                nestedScrollEnabled
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator
              >
                {filteredOptions.length === 0 ? (
                  <Text style={styles.emptyText}>{searching ? 'Searching...' : placeholder}</Text>
                ) : (
                  filteredOptions.map((opt, index) => {
                    const selected = isSelected(opt);
                    return (
                      <TouchableOpacity
                        key={`${String(opt.id)}-${opt.displayText}-${index}`}
                        style={[styles.item, selected && styles.itemSelected]}
                        onPress={() => handleSelect(opt)}
                        activeOpacity={0.7}
                      >
                        {isMulti ? (
                          <View style={[styles.checkbox, selected && styles.checkboxChecked]}>
                            {selected && <MaterialIcons name="check" size={14} color="#FFFFFF" />}
                          </View>
                        ) : null}
                        <Text style={styles.itemText} numberOfLines={1}>
                          {opt.displayText}
                        </Text>
                        {!isMulti && selected && (
                          <MaterialIcons name="check" size={16} color="#0B5FC6" />
                        )}
                      </TouchableOpacity>
                    );
                  })
                )}
              </ScrollView>
            )}

            {isMulti && (
              <View style={styles.footer}>
                <Text style={styles.footerText}>
                  {selectedArray.length} of {options.length} selected
                </Text>
              </View>
            )}
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignSelf: 'flex-start',
  },
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#DEE2E6',
    backgroundColor: '#FFFFFF',
    minHeight: 40,
    minWidth: 140,
  },
  triggerCompact: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 6,
    minHeight: 38,
    minWidth: 118,
  },
  triggerActive: {
    borderColor: AdminColors.blue700,
    backgroundColor: '#F0F6FF',
  },
  triggerDisabled: {
    opacity: 0.5,
    backgroundColor: AdminColors.gray25,
  },
  triggerText: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#2D2F33',
    flex: 1,
    marginRight: 8,
  },
  triggerTextCompact: {
    fontSize: 11,
    marginRight: 6,
  },
  triggerPlaceholder: {
    color: '#7C8086',
  },
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.15)',
  },
  menu: {
    position: Platform.OS === 'web' ? ('fixed' as any) : 'absolute',
    backgroundColor: '#FFFFFF',
    borderRadius: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
    maxHeight: Dimensions.get('window').height * 0.45,
    overflow: 'hidden',
    zIndex: 9999,
  },
  stickyHeader: {
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E9ECEF',
  },
  searchRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F3F5',
  },
  searchInput: {
    flex: 1,
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#2D2F33',
    paddingVertical: 4,
    width: '100%',
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-around',
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  actionsRowCentered: {
    justifyContent: 'center',
  },
  actionButton: {
    paddingVertical: 4,
    paddingHorizontal: 4,
  },
  actionText: {
    fontSize: 12,
    fontFamily: FontFamily.bold,
    color: '#0B5FC6',
  },
  actionDivider: {
    width: 1,
    height: 16,
    backgroundColor: '#DEE2E6',
  },
  list: {
    maxHeight: Dimensions.get('window').height * 0.28,
    ...(Platform.OS === 'web' ? { overflowY: 'auto' } : {}),
    width: '100%',
  } as any,
  listNative: {
    maxHeight: Dimensions.get('window').height * 0.28,
  },
  item: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: 12,
    gap: 10,
  },
  itemSelected: {
    backgroundColor: '#F0F6FF',
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: '#ADB5BD',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
  },
  checkboxChecked: {
    backgroundColor: '#0B5FC6',
    borderColor: '#0B5FC6',
  },
  itemText: {
    flex: 1,
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#343A40',
  },
  emptyText: {
    padding: 16,
    textAlign: 'center',
    fontSize: 13,
    fontFamily: FontFamily.medium,
    color: '#6C757D',
  },
  footer: {
    borderTopWidth: 1,
    borderTopColor: '#E9ECEF',
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#F8F9FA',
  },
  footerText: {
    fontSize: 12,
    fontFamily: FontFamily.medium,
    color: '#6B6E72',
    textAlign: 'center',
  },
});

export default LocationFilterDropdown;
